import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";

interface AiAnalysisModalProps {
  mediaItem: MediaItem;
  isOpen: boolean;
  onClose: () => void;
  anchorElement: HTMLElement | null;
  anchorPosition?: "left" | "right";
}

export const AiAnalysisModal: React.FC<AiAnalysisModalProps> = ({
  mediaItem,
  isOpen,
  onClose,
  anchorElement,
  anchorPosition = "right",
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0, right: 0 });

  useEffect(() => {
    if (!isOpen || !anchorElement) return;

    const updatePosition = () => {
      const rect = anchorElement.getBoundingClientRect();
      setPosition({
        top: rect.top,
        left: rect.left,
        right: window.innerWidth - rect.right,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, anchorElement]);

  if (!isOpen || !mediaItem.frontendData.aiAnalysis) {
    return null;
  }

  const { aiAnalysis } = mediaItem.frontendData;
  const positionStyle = anchorPosition === "left"
    ? { top: position.top - 10, left: position.left, transform: "translateY(-100%)" }
    : { top: position.top - 10, right: position.right, transform: "translateY(-100%)" };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-50"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      <div
        className="fixed w-64 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50"
        style={positionStyle}
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between pb-2 border-b border-gray-200">
            <span className="text-sm font-semibold text-gray-900">AI Analysis</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-1.5 text-xs">
            {aiAnalysis.instagramAppealScore !== null && (
              <div className="flex justify-between">
                <span className="text-gray-600">Instagram Appeal:</span>
                <span className="font-medium text-gray-900">
                  {aiAnalysis.instagramAppealScore.toFixed(1)}
                </span>
              </div>
            )}
            {aiAnalysis.facesScore !== null && (
              <div className="flex justify-between">
                <span className="text-gray-600">Faces Score:</span>
                <span className="font-medium text-gray-900">
                  {aiAnalysis.facesScore.toFixed(1)}
                </span>
              </div>
            )}
            {aiAnalysis.colorAppeal !== null && (
              <div className="flex justify-between">
                <span className="text-gray-600">Color Appeal:</span>
                <span className="font-medium text-gray-900">
                  {aiAnalysis.colorAppeal.toFixed(1)}
                </span>
              </div>
            )}
            {aiAnalysis.compositionQuality !== null && (
              <div className="flex justify-between">
                <span className="text-gray-600">Composition:</span>
                <span className="font-medium text-gray-900">
                  {aiAnalysis.compositionQuality.toFixed(1)}
                </span>
              </div>
            )}
            {aiAnalysis.imageQuality !== null && (
              <div className="flex justify-between">
                <span className="text-gray-600">Image Quality:</span>
                <span className="font-medium text-gray-900">
                  {aiAnalysis.imageQuality.toFixed(1)}
                </span>
              </div>
            )}
            {aiAnalysis.engagementPrediction !== null && (
              <div className="flex justify-between">
                <span className="text-gray-600">Engagement:</span>
                <span className="font-medium text-gray-900">
                  {aiAnalysis.engagementPrediction.toFixed(1)}
                </span>
              </div>
            )}
            {aiAnalysis.labelDescription && (
              <div className="pt-2 mt-2 border-t border-gray-200">
                <span className="text-gray-600 block mb-1">Description:</span>
                <p className="text-gray-900 text-xs leading-relaxed">
                  {aiAnalysis.labelDescription}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};
